<template>
  <div
      class="
      sticky top-0 z-[80] bg-primary-500
      text-zinc-900 border-b border-gray-300
      dark:bg-zinc-900 dark:border-zinc-800
      dark:text-white rounded-none py-2 px-4
"
  >
    <div class="flex w-full min-w-0 flex-row items-center justify-between gap-3 sm:gap-6">
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <div
            class="flex min-w-0 flex-row items-center overflow-hidden"
            :class="mustChangePassword ? 'cursor-default' : 'cursor-pointer'"
            @click="navigateHome"
        >
          <img
              src="/avenue44-logo.png"
              :alt="$t('label.logo')"
              class="max-h-[36px] hidden dark:block"
          >

          <img
              src="/logo-cropped.jpg"
              :alt="$t('label.logo')"
              class="max-h-[36px] block dark:hidden"
          >
        </div>
      </div>

      <div class="flex shrink-0 flex-row items-center gap-1 sm:gap-2">
        <button
            v-if="showMatrixFullscreenButton && !mustChangePassword"
            type="button"
            class="inline-flex size-10 shrink-0 grow-0 cursor-pointer items-center justify-center rounded-full text-[1.0625rem] text-white transition-colors hover:bg-white/18 hover:text-white dark:text-neutral-300 dark:hover:bg-zinc-800 dark:hover:text-white sm:text-[1.1875rem]"
            :aria-label="matrixFullscreenButtonLabel"
            :title="matrixFullscreenButtonLabel"
            @click="requestMatrixFullscreen"
        >
          <i class="far fa-expand"></i>
        </button>
        <button
            v-if="!mustChangePassword"
            type="button"
            class="lg:hidden flex flex-row items-center justify-center text-xl text-neutral-700 dark:text-neutral-300 cursor-pointer hover:bg-zinc-300 dark:hover:bg-zinc-700 rounded-full shrink-0 grow-0 size-10"
            @click="showMenu = true"
        >
          <i class="fas fa-bars"></i>
        </button>
        <button
            v-if="isAuthenticated && mustChangePassword"
            type="button"
            class="inline-flex size-10 shrink-0 grow-0 cursor-pointer items-center justify-center rounded-full text-[1.0625rem] text-white transition-colors hover:bg-white/18 hover:text-white dark:text-neutral-300 dark:hover:bg-zinc-800 dark:hover:text-white sm:text-[1.1875rem]"
            :aria-label="$t('label.logout')"
            @click="$router.replace('/logout')"
        >
          <i class="far fa-sign-out"></i>
        </button>
        <div v-if="isAuthenticated && !mustChangePassword" ref="accountMenuContainer" class="relative hidden lg:block">
          <button
              type="button"
              class="inline-flex size-10 shrink-0 grow-0 cursor-pointer items-center justify-center rounded-full text-[1.0625rem] text-white transition-colors hover:bg-white/18 hover:text-white dark:text-neutral-300 dark:hover:bg-zinc-800 dark:hover:text-white sm:text-[1.1875rem]"
              @click.stop="toggleAccountMenu"
          >
            <i class="fas fa-user"></i>
          </button>

          <transition
              enter-active-class="transition duration-150 ease-out"
              enter-from-class="opacity-0 translate-y-2 scale-95"
              enter-to-class="opacity-100 translate-y-0 scale-100"
              leave-active-class="transition duration-100 ease-in"
              leave-from-class="opacity-100 translate-y-0 scale-100"
              leave-to-class="opacity-0 translate-y-2 scale-95"
          >
            <div
                v-if="showAccountMenu"
                class="absolute right-0 top-[calc(100%+12px)] z-[1200] w-[380px] overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-950"
            >
              <div class="bg-gradient-to-br from-zinc-100 via-white to-zinc-50 px-5 py-5 text-zinc-900 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900 dark:text-white">
                <div class="flex items-center gap-4">
                  <div class="inline-flex size-14 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900">
                    <i class="fas fa-user text-xl"></i>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="text-[11px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
                      {{ $t('menu.account') }}
                    </div>
                    <div class="mt-1 truncate text-xl font-semibold">
                      {{ userDisplayName }}
                    </div>
                    <div class="mt-1 break-words text-sm text-zinc-600 dark:text-zinc-300">
                      {{ user?.email }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="border-t border-zinc-200 px-3 py-3 dark:border-zinc-800">
                <button
                    v-for="action in accountPanelActions"
                    :key="action.label"
                    type="button"
                    class="flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                    @click="executeAccountAction(action)"
                >
                  <div class="flex items-center gap-3">
                    <div class="inline-flex size-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                      <i :class="[action.icon, 'text-base']"></i>
                    </div>
                    <div class="text-sm font-semibold text-zinc-900 dark:text-white">{{ action.label }}</div>
                  </div>
                  <i class="fas fa-chevron-right text-xs text-zinc-400"></i>
                </button>

                <div class="mt-1 overflow-hidden rounded-xl">
                  <button
                      type="button"
                      class="flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                      @click="toggleThemeOptions"
                  >
                    <div class="flex items-center gap-3">
                      <div class="inline-flex size-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                        <i class="far fa-moon text-base"></i>
                      </div>
                      <div>
                        <div class="text-sm font-semibold text-zinc-900 dark:text-white">{{ $t('menu.change_theme') }}</div>
                        <div class="text-xs text-zinc-500 dark:text-zinc-400">{{ activeThemeLabel }}</div>
                      </div>
                    </div>
                    <i
                        class="fas fa-chevron-right text-xs text-zinc-400 transition-transform duration-150"
                        :class="showThemeOptions ? 'rotate-90' : ''"
                    ></i>
                  </button>

                  <div v-if="showThemeOptions" class="ml-6 mr-2 border-l border-zinc-200 pl-3 pb-2 dark:border-zinc-800">
                    <button
                        v-for="theme in themeOptions"
                        :key="theme.value"
                        type="button"
                        class="mt-2 flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                        @click="setThemeMode(theme.value)"
                    >
                      <div class="flex items-center gap-3">
                        <div class="inline-flex size-8 items-center justify-center rounded-lg bg-white text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                          <i :class="[theme.icon, 'text-sm']"></i>
                        </div>
                        <div class="text-sm font-medium text-zinc-900 dark:text-white">{{ theme.label }}</div>
                      </div>
                      <i
                          v-if="themeMode === theme.value"
                          class="fas fa-check text-xs text-zinc-400"
                      ></i>
                    </button>
                  </div>
                </div>

                <div class="mt-1 overflow-hidden rounded-xl">
                  <button
                      type="button"
                      class="flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                      @click="toggleLanguageOptions"
                  >
                    <div class="flex items-center gap-3">
                      <div class="inline-flex size-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                        <i class="far fa-language text-base"></i>
                      </div>
                      <div>
                        <div class="text-sm font-semibold text-zinc-900 dark:text-white">{{ $t('menu.change_language') }}</div>
                        <div class="text-xs text-zinc-500 dark:text-zinc-400">{{ activeLocaleLabel }}</div>
                      </div>
                    </div>
                    <i
                        class="fas fa-chevron-right text-xs text-zinc-400 transition-transform duration-150"
                        :class="showLanguageOptions ? 'rotate-90' : ''"
                    ></i>
                  </button>

                  <div v-if="showLanguageOptions" class="ml-6 mr-2 border-l border-zinc-200 pl-3 pb-2 dark:border-zinc-800">
                    <button
                        v-for="language in languageOptions"
                        :key="language.locale"
                        type="button"
                        class="mt-2 flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                        @click="switchLanguage(language.locale)"
                    >
                      <div class="flex items-center gap-3">
                        <div class="inline-flex size-8 items-center justify-center rounded-lg bg-white text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                          <i :class="[language.icon, 'text-sm']"></i>
                        </div>
                        <div class="text-sm font-medium text-zinc-900 dark:text-white">{{ language.label }}</div>
                      </div>
                      <i
                          v-if="$i18n.locale === language.locale"
                          class="fas fa-check text-xs text-zinc-400"
                      ></i>
                    </button>
                  </div>
                </div>

                <button
                    type="button"
                    class="mt-1 flex w-full cursor-pointer items-center justify-between rounded-xl px-4 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-900"
                    @click="executeAccountAction(logoutAction)"
                >
                  <div class="flex items-center gap-3">
                    <div class="inline-flex size-10 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                      <i :class="[logoutAction.icon, 'text-base']"></i>
                    </div>
                    <div class="text-sm font-semibold text-zinc-900 dark:text-white">{{ logoutAction.label }}</div>
                  </div>
                  <i class="fas fa-chevron-right text-xs text-zinc-400"></i>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
  <Drawer
      v-if="!mustChangePassword"
      v-model:visible="showMenu"
      position="right"
      class="mobile-nav-drawer lg:hidden !w-[22rem] sm:!w-[24rem] max-w-[calc(100vw-3rem)] dark:text-white dark:bg-zinc-900 dark:border-zinc-800"
  >
    <template #container="{ closeCallback }">
      <div class="flex h-full flex-col bg-gradient-to-b from-white via-zinc-50 to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950">
        <div class="shrink-0 border-b border-zinc-200/80 px-5 pb-5 pt-4 dark:border-zinc-800">
          <div class="flex items-center justify-between gap-4">
            <div class="min-w-0">
              <div class="text-3xl font-semibold tracking-[-0.04em] text-zinc-900 dark:text-zinc-50">
                {{ $t('label.menu') }}
              </div>
            </div>
            <button
                type="button"
                class="flex size-11 items-center justify-center rounded-full border border-zinc-200 bg-white/80 text-zinc-700 backdrop-blur-sm hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800"
                @click="closeCallback"
            >
              <i class="fal fa-times"></i>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-4 pb-5 pt-4">
          <ul class="m-0 list-none p-0">
            <li
                v-for="menuItem in mobileMenuItems"
                :key="menuItem.label"
                class="group"
                @click="executeMenuItem(menuItem)"
            >
              <a class="flex cursor-pointer items-center gap-4 rounded-2xl px-3 py-3.5 text-zinc-700 transition-all duration-150 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-50">
                <div class="inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-700 transition-colors group-hover:bg-white dark:bg-zinc-800 dark:text-zinc-200 dark:group-hover:bg-zinc-700">
                  <i :class="[menuItem.icon, 'text-base']"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <span class="block text-base font-semibold tracking-[-0.02em]">{{ menuItem.label }}</span>
                </div>
                <i class="fal fa-arrow-right text-sm text-zinc-300 transition-transform duration-150 group-hover:translate-x-0.5 dark:text-zinc-600"></i>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </template>
  </Drawer>
</template>

<script>
import { Drawer } from "primevue";
import { mapGetters } from "vuex";

const MATRIX_FULLSCREEN_TOOLBAR_STATE_EVENT = 'matrix-fullscreen-toolbar-state'
const MATRIX_FULLSCREEN_TOOLBAR_REQUEST_EVENT = 'matrix-fullscreen-toolbar-request'

export default {
  emits: ['open-qr-scanner'],

  components: {
    Drawer,
  },

  data() {
    return {
      showMenu: false,
      showAccountMenu: false,
      showLanguageOptions: false,
      showThemeOptions: false,
      showMatrixFullscreenButton: false,
      matrixFullscreenButtonLabel: '',
      accountItems: [
        {
          labelKey: 'menu.account',
          icon: 'far fa-user',
          action: () => this.$router.push('/account'),
        },
        {
          labelKey: 'menu.change_password',
          icon: 'far fa-lock',
          action: () => this.$router.push('/account/change-password'),
        },
      ],
    }
  },

  computed: {
    ...mapGetters([
      'user',
      'themeMode',
    ]),

    isAuthenticated() {
      return !!this.$store.getters['session/accessToken']
    },

    mustChangePassword() {
      return this.user?.mustChangePassword === true
    },

    mobileMenuItems() {
      if (this.mustChangePassword) {
        return [
          this.logoutAction,
        ]
      }

      const items = this.translateMenuItems(this.sidebarItems)

      if (!this.isAuthenticated) {
        return items
      }

      return [
        ...items,
        ...this.accountPanelActions.filter(action => action.labelKey !== 'menu.change_password'),
        this.logoutAction,
      ]
    },

    accountPanelActions() {
      return this.translateMenuItems(this.accountItems)
    },

    logoutAction() {
      return this.translateMenuItems([
        {
          labelKey: 'label.logout',
          icon: 'far fa-sign-out',
          action: () => this.$router.replace('/logout'),
        },
      ])[0]
    },

    languageOptions() {
      return this.translateMenuItems([
        {
          labelKey: 'menu.language_nl',
          icon: 'far fa-flag',
          locale: 'nl',
        },
        {
          labelKey: 'menu.language_en',
          icon: 'far fa-flag',
          locale: 'en',
        },
      ])
    },

    activeLocaleLabel() {
      const activeLanguage = this.languageOptions.find(language => language.locale === this.$i18n.locale)

      return activeLanguage?.label ?? this.$i18n.locale
    },

    themeOptions() {
      return [
        {
          label: this.$t('label.theme_dark'),
          icon: 'far fa-moon',
          value: 'dark',
        },
        {
          label: this.$t('label.theme_light'),
          icon: 'far fa-sun',
          value: 'light',
        },
        {
          label: this.$t('label.theme_system'),
          icon: 'far fa-desktop',
          value: 'system',
        },
      ]
    },

    activeThemeLabel() {
      return this.themeOptions.find(theme => theme.value === this.themeMode)?.label ?? this.themeMode
    },

    userDisplayName() {
      return this.user?.name || this.user?.email || this.$t('menu.account')
    },

    sidebarItems() {
      if (this.mustChangePassword) {
        return []
      }

      const items = [
        {
          labelKey: 'menu.projects',
          icon: 'fal fa-tasks',
          needsAuthentication: true,
          action: () => this.$router.push('/'),
        },
        {
          labelKey: 'menu.time_registration',
          icon: 'fal fa-clock',
          needsAuthentication: true,
          action: () => this.$router.push('/uren-registratie'),
        },
        {
          labelKey: 'menu.qr_scan',
          icon: 'fal fa-qrcode',
          needsAuthentication: true,
          action: () => this.$emit('open-qr-scanner'),
          mobileOnly: true,
        },
        {
          labelKey: 'menu.department_management',
          icon: 'fal fa-sitemap',
          needsAuthentication: true,
          action: () => this.$router.push('/afdelingen'),
        },
        {
          labelKey: 'menu.user_management',
          icon: 'fal fa-users-cog',
          needsAuthentication: true,
          action: () => this.$router.push('/gebruikers'),
          roles: ['ROLE_ADMIN'],
        },
      ]

      if (!this.isAuthenticated) {
        return items.filter(menuItem => !menuItem.needsAuthentication)
      }

      let userRoles = []
      if (Array.isArray(this.user?.roles)) {
        userRoles = this.user.roles
      } else if (this.user?.role) {
        userRoles = [this.user.role]
      }

      return items.filter(menuItem => {
        if (menuItem.roles && !menuItem.roles.some(role => userRoles.includes(role))) {
          return false
        }

        return menuItem.needsAuthentication !== false
      })
    },
  },

  methods: {
    translateMenuItems(items) {
      return items.map(menuItem => ({
        ...menuItem,
        label: this.$t(menuItem.labelKey),
      }))
    },

    navigateHome() {
      if (this.mustChangePassword) {
        return
      }

      if (this.$route.path !== '/') {
        this.$router.push('/')
      }
    },

    executeMenuItem(menuItem) {
      menuItem.action()
      this.showMenu = false
    },

    switchLanguage(locale) {
      this.$i18n.locale = locale
      this.$store.dispatch('setLocale', locale)
    },

    toggleLanguageOptions() {
      this.showLanguageOptions = !this.showLanguageOptions
    },

    toggleThemeOptions() {
      this.showThemeOptions = !this.showThemeOptions
    },

    setThemeMode(themeMode) {
      this.$store.dispatch('setThemeMode', themeMode)
    },

    toggleAccountMenu() {
      if (this.showAccountMenu) {
        this.closeAccountMenu()
        return
      }

      this.showAccountMenu = true
    },

    closeAccountMenu() {
      this.showAccountMenu = false
      this.showLanguageOptions = false
      this.showThemeOptions = false
    },

    executeAccountAction(action) {
      this.closeAccountMenu()
      action.action()
    },

    handleClickOutside(event) {
      if (!this.showAccountMenu) {
        return
      }

      const container = this.$refs.accountMenuContainer
      if (container && !container.contains(event.target)) {
        this.closeAccountMenu()
      }
    },

    handleMatrixFullscreenToolbarState(event) {
      this.showMatrixFullscreenButton = Boolean(event?.detail?.visible)
      this.matrixFullscreenButtonLabel = event?.detail?.label || this.$t('matrix.enter_fullscreen')
    },

    requestMatrixFullscreen() {
      globalThis.dispatchEvent(new CustomEvent(MATRIX_FULLSCREEN_TOOLBAR_REQUEST_EVENT))
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    globalThis.addEventListener(MATRIX_FULLSCREEN_TOOLBAR_STATE_EVENT, this.handleMatrixFullscreenToolbarState)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    globalThis.removeEventListener(MATRIX_FULLSCREEN_TOOLBAR_STATE_EVENT, this.handleMatrixFullscreenToolbarState)
  },
}
</script>
